import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronDown } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const Konfigurator = ({ compact = false }) => {
  const [tree, setTree] = useState([]);
  const [brand, setBrand] = useState('');
  const [model, setModel] = useState('');
  const [series, setSeries] = useState('');
  const [category, setCategory] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    fetch(`${API}/api/shop/konfigurator/tree`)
      .then(r => r.json())
      .then(d => setTree(d.brands || []))
      .catch(() => {});
  }, []);

  const selectedBrand = useMemo(() => tree.find(b => b.alias === brand), [tree, brand]);
  const models = selectedBrand?.models || [];
  const selectedModel = useMemo(() => models.find(m => m.alias === model), [models, model]);
  const children = selectedModel?.children || [];

  // Determine if children are series (have sub-children) or direct categories
  const hasSeries = children.some(c => c.children && c.children.length > 0);
  const seriesList = hasSeries ? children : [];
  const selectedSeries = useMemo(() => seriesList.find(s => s.alias === series), [seriesList, series]);
  const categoryList = hasSeries
    ? (selectedSeries?.children || [])
    : children;

  const slugify = (text) =>
    text.toLowerCase().replace(/[äÄ]/g, 'ae').replace(/[öÖ]/g, 'oe')
      .replace(/[üÜ]/g, 'ue').replace(/[ß]/g, 'ss')
      .replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');

  const canGo = category || (!hasSeries && model) || (hasSeries && series && !categoryList.length);

  const handleGo = () => {
    // Build category path for filtering
    let catPath = brand;
    if (model) catPath += `/${model}`;
    if (hasSeries && series) catPath += `/${series}`;
    if (category) catPath += `/${category}`;
    navigate(`/shop?category=${encodeURIComponent(catPath)}`);
    // Scroll to products after navigation with a short delay
    setTimeout(() => {
      const el = document.querySelector('[data-testid="product-grid"], [data-testid="featured-row"]');
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 600);
  };

  return (
    <div className={`konfigurator ${compact ? 'konfigurator-compact' : ''}`} data-testid="konfigurator">
      <h2 className="konfigurator-title">Fahrzeug-Konfigurator</h2>
      <div className="konfigurator-fields">
        <div className="konfigurator-field">
          <label>Marke</label>
          <select
            value={brand}
            onChange={e => { setBrand(e.target.value); setModel(''); setSeries(''); setCategory(''); }}
            data-testid="konfig-brand"
          >
            <option value="">Marke wählen...</option>
            {tree.map(b => <option key={b.alias} value={b.alias}>{b.name}</option>)}
          </select>
        </div>

        <div className="konfigurator-field">
          <label>Modell</label>
          <select
            value={model}
            onChange={e => { setModel(e.target.value); setSeries(''); setCategory(''); }}
            disabled={!brand}
            data-testid="konfig-model"
          >
            <option value="">{brand ? 'Modell wählen...' : 'Bitte zuerst Marke wählen...'}</option>
            {models.map(m => <option key={m.alias} value={m.alias}>{m.name}</option>)}
          </select>
        </div>

        {hasSeries && (
          <div className="konfigurator-field">
            <label>Baureihe / Serie</label>
            <select
              value={series}
              onChange={e => { setSeries(e.target.value); setCategory(''); }}
              disabled={!model}
              data-testid="konfig-series"
            >
              <option value="">{model ? 'Serie wählen...' : 'Erst Modell wählen...'}</option>
              {seriesList.map(s => <option key={s.alias} value={s.alias}>{s.name}</option>)}
            </select>
          </div>
        )}

        {categoryList.length > 0 && (
          <div className="konfigurator-field">
            <label>Kategorie / Zubehör</label>
            <select
              value={category}
              onChange={e => setCategory(e.target.value)}
              disabled={hasSeries ? !series : !model}
              data-testid="konfig-category"
            >
              <option value="">Kategorie wählen...</option>
              {categoryList.map(c => <option key={c.alias} value={c.alias}>{c.name}</option>)}
            </select>
          </div>
        )}
      </div>

      <button
        className="konfigurator-btn"
        disabled={!model}
        onClick={handleGo}
        data-testid="konfig-go-btn"
      >
        Zur Kategorie im Shop
      </button>
    </div>
  );
};

export default Konfigurator;
